import { v4 } from 'uuid'
import type { EvalEventName } from 'v2/utils/event-bus/eval-events'
import { EvalEvents, evalEventBus } from 'v2/utils/event-bus/eval-events'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { useFlowStore } from '../../../store'
import { selectIsGenerating, useSimulationStore } from '../../eval/simulation-store'
import { type EvalChatMessage, EvalMessageStatus, useVibeEvalStore } from './eval-store'

interface EvaluationUpdate {
  payload: {
    eval_event_type: string
    eval_event_data: Record<string, unknown>
    eval_id?: string
    sim_run_uuid?: string
  }
}

const SIM_EVENT_TYPES = new Set([
  'started',
  'simulation_started',
  'scenario_generated',
  'scenario_saved',
  'scenario_db_ready',
  'scenario_started',
  'turn_completed',
  'scenario_completed',
  'simulation_completed',
  'simulation_error',
  'simulation_cancelled',
  'completed',
  'goals_updated',
  // Scenario-generation lifecycle (Vibe Agent path; manual UI feeds the store
  // directly via eval-stream). Routed to useSimulationStore.addEvent so both
  // paths drive the same generation state.
  'progress',
  'error',
])

const parseRunId = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  return undefined
}

/**
 * Find the most recent eval message for a given evalId and optional runId.
 * If runId is provided, tries to match exactly first, then falls back to most recent.
 * This handles cases where 'started' event has no run_id but subsequent events do.
 */
const findEvalMessage = (evalId: string, runId: number | undefined) => {
  const messages = useVibeEvalStore.getState().evalMessages
  const evalMessages = messages.filter((msg) => msg.evalId === evalId)

  if (evalMessages.length === 0) return null

  // If runId provided, try exact match first
  if (runId !== undefined) {
    const exactMatch = evalMessages.find((msg) => msg.evalRunId === runId)
    if (exactMatch) return exactMatch
  }

  // Fall back to most recent message with this evalId (last in array)
  return evalMessages[evalMessages.length - 1]
}

/**
 * Helper function to update evaluation message status
 * Finds the most recent matching message and updates its status
 */
const updateEvalMessageStatus = (
  evalId: string,
  runId: number | undefined,
  status: EvalMessageStatus,
) => {
  const targetMessage = findEvalMessage(evalId, runId)
  if (!targetMessage) return

  const messages = useVibeEvalStore.getState().evalMessages
  const updatedMessages = messages.map((msg) =>
    msg.id === targetMessage.id ? { ...msg, evalStatus: status } : msg,
  )
  useVibeEvalStore.getState().setEvalMessages(updatedMessages)
}

const handleEvaluationUpdate = (update: EvaluationUpdate) => {
  const { eval_event_type, eval_event_data, eval_id, sim_run_uuid } = update.payload || {}

  if (!eval_event_type) {
    logger.error('Invalid evaluation update payload', { eval_event_type, eval_id })
    return
  }

  // Simulation event bridge — route to useSimulationStore
  if (sim_run_uuid || SIM_EVENT_TYPES.has(eval_event_type)) {
    // goals_updated is a legacy Vibe event that populated the removed Conversation Goal tab.
    // Conversation goals are custom metrics now (authored in the Metrics tab against AO judges);
    // nothing reads agentSettings.conversation_goals, so the old handler wrote a dead field and
    // navigated to a tab that never rendered it. It's retired: the event just flows through as a
    // recorded generation event below — no dead-field write, no navigation.

    const simStore = useSimulationStore.getState()
    const wasGenerating = selectIsGenerating(simStore)
    simStore.addEvent(eval_event_type, eval_event_data ?? {})

    if (!wasGenerating && selectIsGenerating(useSimulationStore.getState())) {
      useFlowStore.getState().setRequestedPath('/simulation')
      logger.debug('Scenario generation started via Vibe Agent, switching to scenarios')
    }

    if (eval_event_type === 'started' && sim_run_uuid) {
      useFlowStore.getState().setRequestedPath(`/simulation/results/${sim_run_uuid}`)
      logger.debug('Simulation started via Vibe Agent, switching to simulation results', {
        sim_run_uuid,
      })
    }

    return
  }

  // Legacy per-node eval path — requires eval_id
  if (!eval_id) {
    logger.error('Invalid evaluation update payload — missing eval_id', {
      eval_event_type,
      eval_id,
    })
    return
  }

  // Map vibe-agent eval event types to eval event bus events
  // Note: Backend may send different naming conventions (e.g., colons vs underscores)
  const eventTypeMap: Partial<Record<string, EvalEventName>> = {
    started: EvalEvents.STARTED,
    dataset_generation_started: EvalEvents.DATASET_GENERATION_STARTED,
    dataset_generation_started_node: EvalEvents.DATASET_GENERATION_STARTED_NODE,
    'dataset_generation_started:single_node': EvalEvents.DATASET_GENERATION_STARTED_NODE,
    dataset_generation_completed: EvalEvents.DATASET_GENERATION_COMPLETED,
    dataset_generation_completed_node: EvalEvents.DATASET_GENERATION_COMPLETED,
    'dataset_generation_completed:single_node': EvalEvents.DATASET_GENERATION_COMPLETED,
    dataset_generation_test_case: EvalEvents.DATASET_GENERATION_TEST_CASE,
    evaluation_tests_started_for_node: EvalEvents.TESTS_STARTED_FOR_NODE,
    test_cases_start_for_node: EvalEvents.TESTS_STARTED_FOR_NODE,
    evaluation_result: EvalEvents.RESULT,
    test_case_result: EvalEvents.RESULT,
    eval_cancelled: EvalEvents.CANCELLED,
    node_evaluation_skipped: EvalEvents.NODE_EVALUATION_SKIPPED,
    // Note: node_evaluation_completed is NOT mapped to COMPLETED - that's for individual nodes,
    // not the entire eval. Only 'completed' and 'eval_completed' should trigger the COMPLETED handler.
    completed: EvalEvents.COMPLETED,
    eval_completed: EvalEvents.COMPLETED,
    error: EvalEvents.ERROR,
  }

  const mappedEventType = eventTypeMap[eval_event_type]
  if (mappedEventType) {
    // Bridge event to eval event bus with eval_id in payload
    evalEventBus.emit(mappedEventType, { ...eval_event_data, eval_id })

    logger.debug('Bridged eval event to eval event bus', {
      eval_event_type,
      mappedEventType,
      eval_id,
    })
  }

  // Extract run_id from eval_event_data (default to undefined if not present)
  const run_id = parseRunId(eval_event_data?.run_id)

  // For 'started' events, always create a new message to ensure each eval request
  // gets its own message block (even if run_id is undefined for both)
  if (eval_event_type === 'started') {
    const evalMessage: EvalChatMessage = {
      id: v4(),
      evalId: eval_id,
      evalRunId: run_id,
      evalStatus: EvalMessageStatus.Started,
    }
    useVibeEvalStore.getState().addEvalMessage(evalMessage)
    return
  }

  // For other events, find the most recent matching message
  const existingEvalMessage = findEvalMessage(eval_id, run_id)

  if (!existingEvalMessage) {
    return // No matching message found for this event
  }

  // Update message's evalRunId if it was undefined (created from 'started' event which lacks run_id)
  // This ensures subsequent lookups can do exact matching by run_id
  if (run_id !== undefined && existingEvalMessage.evalRunId === undefined) {
    const messages = useVibeEvalStore.getState().evalMessages
    const updatedMessages = messages.map((msg) =>
      msg.id === existingEvalMessage.id ? { ...msg, evalRunId: run_id } : msg,
    )
    useVibeEvalStore.getState().setEvalMessages(updatedMessages)
    logger.debug('Updated message evalRunId', { messageId: existingEvalMessage.id, run_id })
  }

  if (eval_event_type === 'completed' || eval_event_type === 'eval_completed') {
    updateEvalMessageStatus(eval_id, run_id, EvalMessageStatus.Completed)
  } else if (eval_event_type === 'error') {
    // Update eval message status to error
    updateEvalMessageStatus(eval_id, run_id, EvalMessageStatus.Error)
  } else if (eval_event_type === 'eval_cancelled') {
    // Update eval message status to cancelled
    updateEvalMessageStatus(eval_id, run_id, EvalMessageStatus.Cancelled)
  } else {
    // Update status to running for intermediate events
    const currentStatus = existingEvalMessage.evalStatus
    if (currentStatus === EvalMessageStatus.Started) {
      updateEvalMessageStatus(eval_id, run_id, EvalMessageStatus.Running)
    }
  }
}

export const useEvalEventBridge = () => {
  return {
    handleEvaluationUpdate,
  }
}
